import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik'
import { useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import CityAutocompleteField from '../../../modules/auth/components/fields/CityAutocompleteField'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import RelationAutocompleteField from './fields/RelationsAutocompleteField'
import { MultipleCitiesAutocompleteField } from './fields/MultipleCitiesAutocompleteField'
import { useUserProfileContext } from '../../../context/UserProfileContext'
import { updateProfileConfidential } from '../../../services/profileAdminService'
import { useHistory } from 'react-router-dom'
import { useAlert } from '../../../hooks/utils/useAlert'
import { DatePicker } from 'antd'
import dayjs from 'dayjs'
import { KTSVG } from '../../../../_metronic/helpers'
import { showErrorAlert } from '../../../helpers/alert'
import { useRelationField } from '../../../hooks/components/useRelationField'

const ProfileConfidentialForm: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [selectedCities, setSelecteCities] = useState<string[]>([])
  const [showModal, setShowModal] = useState(false);
  const [messageModal, setMessageModal] = useState("");
  const formikRef = useRef();
  const { data, searchParams, refetch } = useUserProfileContext()
  const navigate = useHistory()
  const { showError } = useAlert()
  const { collection } = useRelationField()

  let initialValues = {
    email: data?.email || '',
    dob: data?.birthday || '',
    phone: data?.phoneNumber || '',
    contactMethods: [
      ...(data?.preferedEmail ? ['email'] : []),
      ...(data?.preferedPhone ? ['phone'] : []),
    ],
    address: data?.postalAddress || '',
    cityCountry: data?.city || '',
    citiesOfInterest: data?.oftenIn || [],
    partnerName: data?.lifePartnerName,
    partnerSurname: data?.lifePartnerLastname,
    relatives: data?.relatives.map((item)=>{
      let relation = collection.map((i)=>i.name == item?.relationship);
      return {
        first_name: item?.firstName,
        last_name: item?.lastName,
        year_of_birth: item?.yearOfBirth,
        relation: relation,
      }  
    })
  }

   useEffect(() => {
    let rel_ = data?.relatives.map((item)=>{
      let relation = collection.filter((i)=>i.name == item?.relationship);
      return {
        first_name: item?.firstName,
        last_name: item?.lastName,
        year_of_birth: item?.yearOfBirth,
        relation: relation.length > 0 ? relation[0] : null,
      }  
    });
    formikRef.current.setFieldValue('relatives',rel_)
   }, [data,collection])

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={
        Yup.object({
          email: Yup.string().email('Invalid email').nullable(),
          phone: Yup.string()
            .matches(/^\+?[0-9()\-\s]{7,15}$/, 'Invalid phone number')
            .required('Phone number is required'),
          dob: Yup.date().required('Date of birth is required'),
          address: Yup.string().trim().required('Address is required').max(200, 'Max 200 characters'),
          citiesOfInterest: Yup.array()
            .of(Yup.string().nullable())
            .nullable(),
          partnerName: Yup.string().nullable().max(100, 'Max 100 characters'),
          partnerSurname: Yup.string().nullable().max(100, 'Max 100 characters'),
          relatives: Yup.array().of(
            Yup.object({
              first_name: Yup.string().max(100, 'Max 100 characters'),
              last_name: Yup.string().max(100, 'Max 100 characters'),
              year_of_birth: Yup.number().nullable(),
              relation: Yup.mixed().nullable(),
            })
          ),
        })
      }
      onSubmit={async (values, { resetForm }) => {
        try {
          setLoading(true)

          const payload = {
            email: values.email,
            birthday: values.dob,
            phone_number: values.phone,
            contact_methods: values.contactMethods,
            address: values.address,
            city_country: values.cityCountry,
            cities_of_interest: values.citiesOfInterest,
            partner: {
              name: values.partnerName,
              surname: values.partnerSurname,
            },
            relatives: (values.relatives || [])
              .filter((r) => r && (r.first_name || r.last_name))
              .map((r) => ({
                first_name: r.first_name || '',
                last_name: r.last_name || '',
                year_of_birth: r.year_of_birth ? Number(r.year_of_birth) : null,
                relationship_id: r.relation?.id ?? (typeof r.relation === 'number' ? r.relation : null),
                relationship: r.relation?.name ?? (typeof r.relation === 'string' ? r.relation : ''),
              })),
          }

          let isSaved = await updateProfileConfidential(payload, (!!searchParams.userSelected ? searchParams.userSelected : ''))
          if (isSaved?.birthday) {
            showErrorAlert({title:'Saved',message:'Your information has been saved successfully.'});
            await refetch()
            navigate.goBack()
          }

          // navigate.push('/biography')
        } catch (error: any) {
          console.error(error)
          const statusCode = error?.response?.status || 500
          showError({
            title: 'Unable to update confidential profile',
            message: "We couldn't save your changes. Please review your information and try again.",
            errorCode: `CONFIDENTIAL_${statusCode}`,
          })
        } finally {
          setLoading(false)
        }
      }}
    >
      {(formik) => {
        const validateSend = async () => {
          const errors = await formik.validateForm();
          const firstErrorPath = findFirstErrorPath(errors);

          if (firstErrorPath) {
            formik.setTouched(touchAll(formik.values))
            setMessageModal(`Please check the field error '${firstErrorPath.replaceAll('_', ' ')}' to save your progress.`)
            const el = document.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement
            setShowModal(true)
            el?.focus()
            return
          }

          formik.submitForm()
        }

        const touchAll = (values: any): any => {
          if (Array.isArray(values)) {
            return values.map((v) => touchAll(v))
          }
          if (typeof values === 'object' && values !== null) {
            return Object.keys(values).reduce((acc, key) => {
              acc[key] = touchAll(values[key])
              return acc
            }, {} as any)
          }
          return true
        }

        const findFirstErrorPath = (errors: any, path = ''): string | null => {
          if (typeof errors === 'string') {
            return path
          }

          if (Array.isArray(errors)) {
            for (let i = 0; i < errors.length; i++) {
              const res = findFirstErrorPath(
                errors[i],
                `#${i+1}`
              )
              if (res) return res
            }
          }

          if (typeof errors === 'object' && errors !== null) {
            for (const key of Object.keys(errors)) {
              const res = findFirstErrorPath(
                errors[key],
                path ? `${key} ${path}` : key
              )
              if (res) return res
            }
          }

          return null
        }
        return (
          <Form className='profile-confidential-form profile-form-base'>
            {/* Header */}
            <div className='confidential-header'>
              <h2 className='confidential-header__title'>Edit Confidential Profile Details</h2>
              <p className='confidential-header__desc'>
                Your contacts and personal details are kept strictly private and used solely by
                Nobilis to facilitate your wellbeing in the community.
              </p>
            </div>

            {/* Section: General Information */}
            <div className='form-section'>
              <h3 className='section-title'>General Information</h3>

              <div className='form-field'>
                <label className='field-label'>Email</label>
                <Field name='email' className='input' disabled />
              </div>

              <div className='form-row'>
                <div className='form-field'>
                  <label className='field-label'>Date of Birth</label>
                  <DatePicker
                    className="underline-picker"
                    format="YYYY-MM-DD"
                    placeholder="YYYY-MM-DD"
                    disabledDate={(current) =>
                      current && current.isAfter(dayjs().subtract(18, 'year').endOf('day'))
                    }
                    value={
                      formik.values.dob
                        ? dayjs(formik.values.dob, 'YYYY-MM-DD')
                        : null
                    }
                    onChange={(value) =>
                      formik.setFieldValue(
                        'dob',
                        value ? value.format('YYYY-MM-DD') : null
                      )
                    }
                  />
                  <ErrorMessage name={`dob`} component="div" className="tap-add-experience-text text-danger" />
                </div>

                <div className='form-field'>
                  <label className='field-label'>Phone</label>
                  <div className='profile-input-wrapper'>
                    <Field name='phone'>
                      {({ field, form }: any) => (
                        <PhoneInput
                          country='us'
                          placeholder='+1'
                          containerClass='profile-input'
                          value={field.value || ''}
                          onChange={(val: string) => form.setFieldValue(field.name, val)}
                          onBlur={() => form.setFieldTouched(field.name, true)}
                        />
                      )}
                    </Field>
                  </div>
                  <ErrorMessage name={`phone`} component="div" className="tap-add-experience-text text-danger" />
                </div>
              </div>

              <div className='form-field'>
                <span className='field-subtitle'>Preferred Contact Method</span>
                <div className='checkbox-group'>
                  <label>
                    <Field type='checkbox' name='contactMethods' value='email' />
                    Email
                  </label>
                  <label>
                    <Field type='checkbox' name='contactMethods' value='phone' />
                    Phone
                  </label>
                </div>
              </div>
            </div>

            {/* Section: Postal Address */}
            <div className='form-section'>
              <div className='section-header'>
                <h3 className='section-title'>Postal Address</h3>
                <p className='section-desc'>
                  Required for exclusive Nobilis mailings: letters, invitations, gifts, and more.
                </p>
              </div>

              <div className='form-field'>
                <label className='field-label'>Address</label>
                <Field name='address' className='input' />
                <ErrorMessage name='address' component="div" className="tap-add-experience-text text-danger" />
              </div>

              <div className='form-field'>
                <label className='field-label'>City & Country</label>
                <CityAutocompleteField name='cityCountry' />
              </div>
            </div>

            {/* Section: Cities of Interest */}
            <div className='form-section'>
              <div className='section-header'>
                <h3 className='section-title'>Cities of Interest – often in</h3>
                <p className='section-desc'>
                  Add cities you frequently visit or stay in, aside from your primary residence.
                </p>
              </div>
              <FieldArray name="citiesOfInterest">
                {({ push, remove }) => (<>
                  {(formik.values.citiesOfInterest ?? []).map((item, index) => (<div key={`cities-items-${index}-1`} className='w-100'>
                    <div className='d-flex flex-row'>
                      <CityAutocompleteField key={`confidentials-cities-${index}`} name={`citiesOfInterest[${index}]`} />
                      {index > 0 && (
                        <button type="button" onClick={() => remove(index)} className="remove-btn tap-flex-center">
                          <KTSVG path='/media/svg/nobilis/teams_and_partner/team_and_partner_experiences_add_experience_availability_date_delete.svg' />&nbsp;
                        </button>
                      )}
                    </div>
                    <ErrorMessage key={`cities-items-${index}-2`} name={`citiesOfInterest[${index}]`} component="div" className="tap-add-experience-text text-danger" />
                  </div>))}
                  <div className='form-add' onClick={() => push('')}>
                    + Add more
                  </div>
                </>)}
              </FieldArray>
            </div>
            <ErrorMessage name={`citiesOfInterest`} component="div" className="tap-add-experience-text text-danger" />

            {/* Section: Family Relations */}
            <div className='form-section'>
              <h3 className='section-title'>Family Relations</h3>

              <div className='section-header'>
                <span className='field-label-part'>YOUR LIFE PARTNER (If applicable)</span>
                <p className='section-desc'>
                  If your partner meets Nobilis’ qualifications, they are eligible for a Life Partner
                  Discount on their own Nobilis account. If not, or not willing to create the account,
                  they can be linked to your profile and join you in Nobilis Experiences alongside
                  your closest relatives (parents, siblings) and children (up to 21 years old).
                  <br />
                  <br />
                  Relationships must have lasted at least one year to qualify.
                </p>
              </div>

              <div className='form-row'>
                <div className='form-field'>
                  <label className='field-label'>Name</label>
                  <Field name='partnerName' className='input' />
                  <ErrorMessage name="partnerName" component="div" className="tap-add-experience-text text-danger" />
                </div>
                <div className='form-field'>
                  <label className='field-label'>Surname</label>
                  <Field name='partnerSurname' className='input' />
                  <ErrorMessage name="partnerSurname" component="div" className="tap-add-experience-text text-danger" />
                </div>
              </div>
            </div>

            {/* Section: Relatives */}
            <div className='form-section'>
              <div className='section-header'>
                <h3 className='section-title'>Your Relatives</h3>
                <p className='section-desc'>
                  Nobilis supports you and your family in enjoying quality time together and within
                  the community. To include your loved ones, please register your closest relatives.
                </p>
              </div>

              <FieldArray name="relatives">
                {({ push, remove }) => (
                  <>
                    {(formik.values.relatives ?? []).map((relative, index) => (
                      <div key={index} className="form-grid-2x2 rm-style-84 flex-column">
                        <div className='d-flex flex-row w-100 gap-6'>
                          <div className="form-field ml-2 mr-2">
                            <label>Name</label>
                            <Field name={`relatives[${index}].first_name`} className="input" />
                            <ErrorMessage name={`relatives[${index}].first_name`} component="div" className="tap-add-experience-text text-danger" />
                          </div>

                          <div className="form-field ml-2 mr-2">
                            <div className='d-flex flex-row justify-content-between'>
                              <label>Surname</label>
                              {index > 0 && (
                                <button type="button" onClick={() => remove(index)} className="remove-btn tap-flex-center">
                                  <KTSVG path='/media/svg/nobilis/teams_and_partner/team_and_partner_experiences_add_experience_availability_date_delete.svg' />&nbsp;
                                </button>
                              )}
                            </div>
                            <Field name={`relatives[${index}].last_name`} className="input" />
                            <ErrorMessage name={`relatives[${index}].last_name`} component="div" className="tap-add-experience-text text-danger" />
                          </div>
                        </div>
                        <div className='d-flex flex-row w-100 gap-6'>
                          <div className="form-field">
                            <label>Year of Birth</label>
                            <DatePicker picker="year" className="underline-picker"
                              disabledDate={(current) =>
                                current && current.isAfter(dayjs().endOf('day'))
                              }
                              value={
                                relative.year_of_birth
                                  ? dayjs(
                                    relative.year_of_birth.toString(),
                                    'YYYY'
                                  )
                                  : null
                              }
                              onChange={(value) => formik.setFieldValue(`relatives[${index}].year_of_birth`, (value ? value.year() : null))}
                            />
                            <ErrorMessage name={`relatives[${index}].year_of_birth`} component="div" className="tap-add-experience-text text-danger" />
                          </div>

                          <div className="form-field">
                            <label>Relation</label>
                            <RelationAutocompleteField name={`relatives[${index}].relation`} />
                            <ErrorMessage name={`relatives[${index}].relation`} component="div" className="tap-add-experience-text text-danger" />
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="form-add"
                      onClick={() => push({ first_name: '', last_name: '', year_of_birth: null, relation: { id: null, name: '' } })}>
                      + Add more relatives
                    </div>
                  </>
                )}
              </FieldArray>
            </div>

            {/* Actions */}
            <div className='form-actions'>
              <button type='button' className='btn-secondary' onClick={() => navigate.goBack()}>
                Cancel
              </button>
              <button type='button' className='btn nb-btn-primary' disabled={loading} aria-busy={loading ? 'true' : 'false'} onClick={validateSend}>
                {!loading ? (
                  <>
                    <span className='nb-heading-md'>save changes</span>
                    <img src='/media/svg/nobilis/vector1.svg' alt='' className='nb-btn-icon nb-btn-icon--white'/>
                  </>
                ) : (
                  <span className='indicator-progress nb-heading-md'>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' role='status' aria-hidden='true'/>
                  </span>
                )}
              </button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ProfileConfidentialForm
